import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import {
  fetchInterestProductIds,
  saveCatalogInterest,
  removeCatalogInterest,
  hasCatalogWhatsapp,
  productInterestPayload,
} from './catalogInterestApi';

export function useCatalogInterests(slug) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (localStorage.getItem('catalog_token')) {
      localStorage.removeItem('catalog_token');
      localStorage.removeItem('catalog_user');
    }
  }, []);

  const [savedIds, setSavedIds] = useState(() => new Set());
  const [loadingIds, setLoadingIds] = useState(false);
  const [pendingProductId, setPendingProductId] = useState(null);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [toast, setToast] = useState('');

  const refreshIds = useCallback(async () => {
    const hasToken = !!localStorage.getItem('token');
    if (!slug || !hasToken) {
      setSavedIds(new Set());
      return;
    }
    setLoadingIds(true);
    try {
      const data = await fetchInterestProductIds(slug);
      setSavedIds(new Set(data?.productIds || []));
    } catch {
      setSavedIds(new Set());
    } finally {
      setLoadingIds(false);
    }
  }, [slug]);

  useEffect(() => {
    refreshIds();
  }, [refreshIds]);

  const isSaved = useCallback(
    (productId) => savedIds.has(productId),
    [savedIds]
  );

  const persistInterest = useCallback(
    async (product) => {
      if (!product?.id || !slug) return false;
      try {
        await saveCatalogInterest({
          slug,
          productId: product.id,
          ...productInterestPayload(product),
        });
        setSavedIds((prev) => new Set([...prev, product.id]));
        setToast('Interés guardado');
        return true;
      } catch (err) {
        const msg = err.response?.data?.message || 'No se pudo guardar el interés';
        setToast(msg);
        return false;
      }
    },
    [slug]
  );

  const needsWhatsappPrompt = useCallback(() => {
    if (!localStorage.getItem('token')) return false;
    let currentUser = user;
    try {
      const stored = JSON.parse(localStorage.getItem('user') || 'null');
      if (stored) currentUser = stored;
    } catch {
      /* ignore */
    }
    return !hasCatalogWhatsapp(currentUser);
  }, [user]);

  const saveAfterAuth = useCallback(
    async (product, { onNeedWhatsapp } = {}) => {
      if (!product?.id || !slug) return false;
      if (needsWhatsappPrompt()) {
        setPendingProduct(product);
        setPendingProductId(product.id);
        onNeedWhatsapp?.(product);
        return false;
      }
      return persistInterest(product);
    },
    [slug, needsWhatsappPrompt, persistInterest]
  );

  const saveWithOptionalChecks = useCallback(
    async (product, { onNeedAuth, onNeedWhatsapp } = {}) => {
      if (!product?.id) return { needsAuth: false, needsWhatsapp: false };

      if (!localStorage.getItem('token')) {
        setPendingProduct(product);
        setPendingProductId(product.id);
        onNeedAuth?.(product);
        return { needsAuth: true, needsWhatsapp: false };
      }

      if (savedIds.has(product.id)) {
        try {
          await removeCatalogInterest(slug, product.id);
          setSavedIds((prev) => {
            const next = new Set(prev);
            next.delete(product.id);
            return next;
          });
          setToast('Interés quitado');
        } catch {
          setToast('No se pudo quitar el interés');
        }
        return { needsAuth: false, needsWhatsapp: false };
      }

      if (needsWhatsappPrompt()) {
        setPendingProduct(product);
        setPendingProductId(product.id);
        onNeedWhatsapp?.(product);
        return { needsAuth: false, needsWhatsapp: true };
      }

      await persistInterest(product);
      return { needsAuth: false, needsWhatsapp: false };
    },
    [isAuthenticated, savedIds, slug, needsWhatsappPrompt, persistInterest]
  );

  const toggleInterest = useCallback(
    async (product, handlers = {}) =>
      saveWithOptionalChecks(product, handlers),
    [saveWithOptionalChecks]
  );

  const completePendingSave = useCallback(async () => {
    const product = pendingProduct;
    if (!product?.id) return false;
    const ok = await persistInterest(product);
    if (ok) {
      setPendingProduct(null);
      setPendingProductId(null);
    }
    return ok;
  }, [pendingProduct, persistInterest]);

  const clearPending = useCallback(() => {
    setPendingProductId(null);
    setPendingProduct(null);
  }, []);

  const getPendingProduct = useCallback(
    (productos) => {
      if (pendingProduct) return pendingProduct;
      if (!pendingProductId) return null;
      return productos?.find((p) => p.id === pendingProductId) || { id: pendingProductId };
    },
    [pendingProduct, pendingProductId]
  );

  return {
    isAuthenticated,
    isSaved,
    loadingIds,
    toggleInterest,
    saveAfterAuth,
    saveWithOptionalChecks,
    completePendingSave,
    refreshIds,
    pendingProductId,
    pendingProduct,
    clearPending,
    getPendingProduct,
    needsWhatsappPrompt,
    toast,
    setToast,
  };
}
